import { db } from '../firebase';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';

// Fallback data for subscription plans
const fallbackPlans = {
  plan_1: {
    id: 'plan_1',
    name: 'Starter',
    description: 'Perfect for testing and light content updates',
    price: 9.99,
    features: [
      'AI Generation',
      'Price Tools',
      'Up to 14 product edits/month',
      '100 credits per month',
      'Edit up to 5 products at once'
    ],
    enabled: true,
    sortOrder: 1,
    maxProductSelection: 14,
    monthlyCredits: 100,
    planLevel: 1
  },
  plan_2: {
    id: 'plan_2',
    name: 'Standard',
    description: 'Ideal for smaller shops and regular listings',
    price: 19.99,
    features: [
      'AI Generation',
      'Price Tools',
      'Inventory Tools',
      'Up to 35 product edits/month',
      '250 credits per month',
      'Edit up to 15 products at once'
    ],
    enabled: true,
    sortOrder: 2,
    maxProductSelection: 35,
    monthlyCredits: 250,
    planLevel: 2
  },
  plan_3: {
    id: 'plan_3',
    name: 'Premium',
    description: 'Great for growing stores and weekly batches',
    price: 49.99,
    features: [
      'AI Generation',
      'Price Tools',
      'Inventory Tools',
      'Product Tools',
      'Up to 100 product edits/month',
      '700 credits per month',
      'Edit up to 25 products at once'
    ],
    enabled: true,
    sortOrder: 3,
    maxProductSelection: 100,
    monthlyCredits: 700,
    planLevel: 3,
    mostPopular: true
  },
  plan_4: {
    id: 'plan_4',
    name: 'Enterprise',
    description: 'Designed for active catalogs with frequent updates',
    price: 99.99,
    features: [
      'AI Generation',
      'Price Tools',
      'Inventory Tools',
      'Product Tools',
      'Priority Support',
      'Up to 230 product edits/month',
      '1600 credits per month',
      'Edit up to 50 products at once'
    ],
    enabled: true,
    sortOrder: 4,
    maxProductSelection: 230,
    monthlyCredits: 1600,
    planLevel: 4
  },
  plan_5: {
    id: 'plan_5',
    name: 'Ultimate',
    description: 'Built for high-volume sellers and automation',
    price: 199.99,
    features: [
      'AI Generation',
      'Price Tools',
      'Inventory Tools',
      'Product Tools',
      'Dedicated Support',
      'Up to 500 product edits/month',
      '3500 credits per month',
      'Edit up to 100 products at once'
    ],
    enabled: true,
    sortOrder: 5,
    maxProductSelection: 500,
    monthlyCredits: 3500,
    planLevel: 5
  }
};

// Fallback data for flex credit packages if Firebase access fails
const fallbackPackages = {
  package_1: {
    id: 'package_1',
    name: 'Small Boost',
    price: 7.99,
    credits: 100,
    productLimit: 14,
    description: 'Perfect for quick fixes',
    popular: false,
    enabled: true,
    sortOrder: 1
  },
  package_2: {
    id: 'package_2',
    name: 'Medium Boost',
    price: 17.99,
    credits: 250,
    productLimit: 35,
    description: 'Ideal for flash updates or new arrivals',
    popular: false,
    enabled: true,
    sortOrder: 2
  },
  package_3: {
    id: 'package_3',
    name: 'Large Boost',
    price: 39.99,
    credits: 600,
    productLimit: 75,
    description: 'Scale faster during seasonal pushes',
    popular: true,
    enabled: true,
    sortOrder: 3
  },
  package_4: {
    id: 'package_4',
    name: 'XL Boost',
    price: 74.99,
    credits: 1250,
    productLimit: 175,
    description: 'Great for monthly syncs or content sprints',
    popular: false,
    enabled: true,
    sortOrder: 4
  },
  package_5: {
    id: 'package_5',
    name: 'XXL Boost',
    price: 149.99,
    credits: 2500,
    productLimit: 350,
    description: 'Built for large dropshipping and agency-scale needs',
    popular: false,
    enabled: true,
    sortOrder: 5
  }
};

// Fallback data for tool descriptions
const fallbackToolDescriptions = {
  priceTools: `**Bulk Price Updates** - Modify prices across multiple products at once using percentage or fixed amount adjustments\n\n**Price Change Types** - Increase, decrease, or set specific prices with flexible options\n\n**Compare-at-Price Management** - Easily set and adjust compare-at prices to show savings\n\n**Price Rounding** - Automatically round prices to maintain consistent pricing patterns\n\n**Save & Load Settings** - Save pricing strategies for future use`,
  inventoryTools: `**Bulk Inventory Updates** - Update inventory quantities across multiple products simultaneously\n\n**Selective Updates** - Option to ignore products with zero inventory\n\n**Save & Load Settings** - Store inventory management preferences`,
  productTools: `**Handle Management** - Generate custom handles with prefixes/suffixes or empty handles for Shopify auto-generation\n\n**Vendor Management** - Update vendor information across multiple products\n\n**SKU Generator** - Create SKUs for all variants using customizable format with product title, variant options, and prefixes/suffixes\n\n**Save & Load Settings** - Store product management preferences for future use`
};

/**
 * Fetches subscription plans for public display (landing page)
 */
export async function getPublicSubscriptionPlans() {
  try {
    // Try to get subscription plans from Firestore
    const plansQuery = query(collection(db, 'subscriptionPlans'), where('enabled', '==', true));
    const plansSnapshot = await getDocs(plansQuery);
    
    const plans = {};
    plansSnapshot.forEach((doc) => {
      const planData = doc.data();
      plans[doc.id] = {
        ...planData,
        id: doc.id
      };
    });
    
    // Try to get active sales information
    const salesDoc = await getDoc(doc(db, 'settings', 'sales'));
    const activeSales = salesDoc.exists() ? salesDoc.data() : {
      subscription: { active: false, name: '', percentage: 0 }
    };
    
    return { plans: Object.keys(plans).length > 0 ? plans : fallbackPlans, activeSales };
  } catch (error) {
    console.error('Error fetching public subscription plans:', error);
    // Return fallback data if Firebase access fails
    return { 
      plans: fallbackPlans, 
      activeSales: {
        subscription: { active: false, name: '', percentage: 0 }
      }
    };
  }
}

/**
 * Fetches flex credit packages for public display (landing page)
 */
export async function getPublicFlexCreditPackages() {
  try {
    // Try to get flex credit packages from Firestore
    const packagesQuery = query(collection(db, 'flexCreditPackages'), where('enabled', '==', true));
    const packagesSnapshot = await getDocs(packagesQuery);
    
    const packages = {};
    packagesSnapshot.forEach((doc) => {
      const packageData = doc.data();
      packages[doc.id] = {
        ...packageData,
        id: doc.id
      };
    });
    
    // Try to get active sales information
    const salesDoc = await getDoc(doc(db, 'settings', 'sales'));
    const activeSales = salesDoc.exists() ? salesDoc.data() : {
      flexCredits: { active: false, name: '', percentage: 0 }
    };
    
    // If we have packages from Firestore, use them; otherwise use fallback
    const finalPackages = Object.keys(packages).length > 0 ? packages : fallbackPackages;
    
    return { packages: finalPackages, activeSales };
  } catch (error) {
    console.error('Error fetching public flex credit packages:', error);
    // Return fallback data if Firebase access fails
    return { 
      packages: fallbackPackages,
      activeSales: {
        flexCredits: { active: false, name: '', percentage: 0 }
      }
    };
  }
}

/**
 * Fetches tool descriptions for public display (landing page)
 */
export async function getPublicToolDescriptions() {
  try {
    const toolsDoc = await getDoc(doc(db, 'settings', 'toolDescriptions'));
    if (toolsDoc.exists()) {
      return toolsDoc.data();
    }
    return fallbackToolDescriptions;
  } catch (error) {
    console.error('Error fetching public tool descriptions:', error);
    return fallbackToolDescriptions;
  }
}
